import React, { useMemo, useState } from "react";

import { faChevronLeft, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { List, ListItemText, TextField } from "@material-ui/core";
import sortBy from "lodash/sortBy";
import { useSelector } from "react-redux";

import { space } from "constants/theme";
import { PickerListItem } from "components/PickerListItem";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SecondaryButton } from "components/Form";
import { SaleType, UnselectableSaleTypes } from "constants/sale";
import { Species } from "constants/species";
import { openSaleModal } from "lib/navigation";
import {
  getSaleSubTypes,
  getSaleTypes,
  getSpecies,
  selectCurrentSpecies,
} from "selectors";
import { toTitleCase } from "lib";
import { SmallFaIcon } from "components/FaIcon";

const getTitle = (
  phase,
  saleTypePhase,
  selectedSaleType,
  selectedSpeciesName,
) => {
  if (phase === saleTypePhase.SALE_TYPE) {
    return "Select Sale Type";
  } else if (phase === saleTypePhase.SPECIES) {
    return `${selectedSaleType} > Select Species`;
  } else {
    return `${selectedSaleType} > ${selectedSpeciesName !== selectedSaleType ? `${toTitleCase(selectedSpeciesName)} >` : ""} Select Sub Sale Type`;
  }
};

// Paddock and Over Hooks are shown first, remaining types follow enum order.
const PRIORITY_SALE_TYPES = [SaleType.PADDOCK, SaleType.OVER_HOOKS];

const ORDERED_SALE_TYPES = [
  ...PRIORITY_SALE_TYPES,
  ...Object.values(SaleType).filter(st => !PRIORITY_SALE_TYPES.includes(st)),
];

// Sale types whose species is fixed by the backend — species selection is skipped for these.
// Mirrors the logic in SaleModal's handleReInit and onSubmit.
const FIXED_SPECIES_BY_SALE_TYPE: Partial<Record<SaleType, number>> = {
  [SaleType.CLEARING]: Species.CLEARING_SALE,
  [SaleType.SUNDRY_SALE]: Species.SUNDRY_SALE,
};

const saleTypePhase = {
  SALE_TYPE: "SALE_TYPE",
  SPECIES: "SPECIES",
  SALE_SUB_TYPE: "SALE_SUB_TYPE",
};

type Phase = (typeof saleTypePhase)[keyof typeof saleTypePhase];

interface SaleTypePickerModalProps {
  onClose: () => void;
}

export function SaleTypePickerModal({
  onClose,
}: SaleTypePickerModalProps): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>(saleTypePhase.SALE_TYPE);
  const [selectedSaleType, setSelectedSaleType] = useState<string | null>(null);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(
    null,
  );
  const [search, setSearch] = useState("");

  const availableSaleTypes: string[] = useSelector(getSaleTypes);
  const saleSubTypesById: Record<string, SaleSubType> =
    useSelector(getSaleSubTypes);
  const currentSpecies: Species[] = useSelector(selectCurrentSpecies);
  const speciesByIdLookup: Record<number, Species> = useSelector(getSpecies);

  // Phase 1: sale type options
  const allSaleTypeOptions = ORDERED_SALE_TYPES.filter(
    saleType => !UnselectableSaleTypes.includes(saleType),
  ).map(saleType => ({
    value: saleType,
    isDisabled: !availableSaleTypes.includes(saleType),
  }));

  const filteredSaleTypeOptions = allSaleTypeOptions.filter(({ value }) =>
    value.toLowerCase().includes(search.toLowerCase()),
  );

  // Phase 2: all species available to the current role, sorted alphabetically
  const sortedSpecies = useMemo(
    () => sortBy(currentSpecies, "name"),
    [currentSpecies],
  );

  const filteredSpeciesOptions = sortedSpecies.filter(species =>
    species.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Phase 3: sub-sale type options filtered by selected sale type and species
  const subTypesForSelection = useMemo(() => {
    if (!selectedSaleType || !selectedSpeciesId) {
      return [];
    }
    return sortBy(
      Object.values(saleSubTypesById).filter(
        (st: SaleSubType) =>
          st.saleType === selectedSaleType &&
          st.speciesId === selectedSpeciesId,
      ),
      "name",
    );
  }, [selectedSaleType, selectedSpeciesId, saleSubTypesById]);

  const filteredSubTypeOptions = subTypesForSelection.filter(st =>
    st.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelectSaleType = (saleType: string) => {
    const fixedSpeciesId = FIXED_SPECIES_BY_SALE_TYPE[saleType as SaleType];

    if (fixedSpeciesId !== undefined) {
      // Species is determined by the sale type — skip phase 2.
      const hasSubTypes = Object.values(saleSubTypesById).some(
        (st: SaleSubType) =>
          st.saleType === saleType && st.speciesId === fixedSpeciesId,
      );
      if (hasSubTypes) {
        setSelectedSaleType(saleType);
        setSelectedSpeciesId(fixedSpeciesId);
        setSearch("");
        setPhase(saleTypePhase.SALE_SUB_TYPE);
      } else {
        onClose();
        openSaleModal(null, "", {
          sale_type: saleType,
          species_id: fixedSpeciesId,
        });
      }
    } else {
      setSelectedSaleType(saleType);
      setSearch("");
      setPhase(saleTypePhase.SPECIES);
    }
  };

  const handleSelectSpecies = (speciesId: number) => {
    const hasSubTypes = Object.values(saleSubTypesById).some(
      (st: SaleSubType) =>
        st.saleType === selectedSaleType && st.speciesId === speciesId,
    );
    if (!hasSubTypes) {
      // No sub-types for this combination — open the sale modal directly
      onClose();
      openSaleModal(null, "", {
        sale_type: selectedSaleType,
        species_id: speciesId,
      });
    } else {
      setSelectedSpeciesId(speciesId);
      setSearch("");
      setPhase(saleTypePhase.SALE_SUB_TYPE);
    }
  };

  const handleSelectSubType = (subTypeId: number) => {
    onClose();
    openSaleModal(null, "", {
      sale_type: selectedSaleType,
      species_id: selectedSpeciesId,
      sale_sub_type_id: subTypeId,
    });
  };

  const handleBack = () => {
    setSearch("");
    if (phase === saleTypePhase.SALE_SUB_TYPE) {
      setSelectedSpeciesId(null);
      // If species was fixed by the sale type, go back to phase 1 (phase 2 was skipped)
      const wasFixedSpecies =
        selectedSaleType !== null &&
        FIXED_SPECIES_BY_SALE_TYPE[selectedSaleType as SaleType] !== undefined;
      if (wasFixedSpecies) {
        setSelectedSaleType(null);
        setPhase(saleTypePhase.SALE_TYPE);
      } else {
        setPhase(saleTypePhase.SPECIES);
      }
    } else if (phase === saleTypePhase.SPECIES) {
      setSelectedSaleType(null);
      setPhase(saleTypePhase.SALE_TYPE);
    }
  };

  const selectedSpeciesName = speciesByIdLookup[selectedSpeciesId]?.name;

  const title = getTitle(
    phase,
    saleTypePhase,
    selectedSaleType,
    selectedSpeciesName,
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          placeholder="Tap to search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          fullWidth
          style={{ marginBottom: space[2] }}
          inputProps={{ "aria-label": "search" }}
        />
        <List data-tour="sale-type-picker" disablePadding>
          {phase === saleTypePhase.SALE_TYPE &&
            filteredSaleTypeOptions.map(({ value, isDisabled }) => (
              <PickerListItem
                key={value}
                data-tour="sale-type-picker-item"
                button={!isDisabled as true}
                disabled={isDisabled}
                onClick={
                  isDisabled ? undefined : () => handleSelectSaleType(value)
                }
              >
                <ListItemText
                  primary={
                    isDisabled ? (
                      <>
                        <SmallFaIcon
                          color="gray44"
                          icon={faLock}
                          className="px-4"
                        />
                        {value}
                      </>
                    ) : (
                      value
                    )
                  }
                />
              </PickerListItem>
            ))}

          {phase === saleTypePhase.SPECIES &&
            filteredSpeciesOptions.map(species => (
              <PickerListItem
                key={species.id}
                data-tour="sale-type-picker-item"
                button
                onClick={() => handleSelectSpecies(species.id)}
              >
                <ListItemText primary={toTitleCase(species.name)} />
              </PickerListItem>
            ))}

          {phase === saleTypePhase.SALE_SUB_TYPE &&
            filteredSubTypeOptions.map(subType => (
              <PickerListItem
                key={subType.id}
                data-tour="sale-type-picker-item"
                button
                onClick={() => handleSelectSubType(subType.id)}
              >
                <ListItemText primary={toTitleCase(subType.name)} />
              </PickerListItem>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        {phase !== saleTypePhase.SALE_TYPE && (
          <SecondaryButton onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
            &nbsp;Back
          </SecondaryButton>
        )}
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
}
